import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { MapPinIcon } from "@/components/icons";
import { regions } from "@/constants/regions";
import { useUser, useUserUpadateUser } from "@/features/auth/repositories/auth-repository";
import { BusinessDetailsSchema, TBusinessDetailsSchema } from "@/features/settings/forms/schema";
import { buildAccountFormData } from "@/features/settings/account-payload";

import useGeoLocation from "@/hooks/useGeoLocation";
import Button from "@/components/buttons/Button";
import IconButton from "@/components/buttons/IconButton";
import SelectInput from "@/components/form-controls/SelectInput";
import TextInput from "@/components/form-controls/TextInput";
import Label from "@/components/form-controls/Label";
import PhoneNumberInput from "@/components/form-controls/PhoneInput";


const regionOptions = regions.map((region) => ({ label: region.name, value: region.name }))

const districtOptions = (name?: string) => {
    const region = regions.find((el) => el.name == name)

    if (region) return region.districts.map((district) => ({ label: district.name, value: district.name }))

    return []
}


export default function BusinessDetails() {
    const { user, isLoading } = useUser();
    const account = user?.results;
    const business = account?.business;

    const { location, getLocation, isLoading: isLocating } = useGeoLocation();
    const { mutate: updateUser, isPending } = useUserUpadateUser();

    const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(BusinessDetailsSchema),
        values: {
            businessName: business?.businessName ?? "",
            businessTradingName: business?.businessTradingName ?? "",
            businessTinNumber: business?.businessTinNumber ?? "",
            businessRegion: business?.businessRegion ?? "",
            businessDistrict: business?.businessDistrict ?? "",
            businessStreet: business?.businessStreet ?? "",
            businessLat: business?.businessLat ?? "",
            businessLng: business?.businessLng ?? "",
            businessManager: business?.businessManager ?? "",
            businessPhone: business?.businessPhone ?? "",
            businessEmail: business?.businessEmail ?? ""
        },
        resetOptions: { keepDirtyValues: true }
    });

    useEffect(() => {
        if (location) {
            setValue("businessLat", location.coords.latitude.toString(), { shouldDirty: true });
            setValue("businessLng", location.coords.longitude.toString(), { shouldDirty: true });
        }
    }, [location, setValue])

    const submit = (data: TBusinessDetailsSchema) => {
        if (!account) return;

        updateUser({
            id: account.id,
            input: buildAccountFormData(account, { business: data })
        });
    }

    return (
        <div className="w-full py-3 pr-3 space-y-6">
            <div>
                <h1 className="text-lg font-semibold">Business Details</h1>
                <p className="text-xs text-secondary-300">
                    The registered business ZCT issues your licences to.
                </p>
            </div>

            <form className="grid grid-cols-2 gap-4" onSubmit={handleSubmit(submit)}>
                <TextInput
                    label="Business Name"
                    placeholder="enter your registered business name"
                    error={errors.businessName?.message?.toString()}
                    register={register("businessName")}
                    required
                />

                <TextInput
                    label="Trading Name"
                    placeholder="enter your trading name"
                    error={errors.businessTradingName?.message?.toString()}
                    register={register("businessTradingName")}
                />

                <TextInput
                    label="TIN Number"
                    placeholder="enter your tin number"
                    className="col-span-2"
                    error={errors.businessTinNumber?.message?.toString()}
                    register={register("businessTinNumber")}
                />

                <SelectInput
                    label="Region"
                    displayName="label"
                    valueName="value"
                    options={regionOptions}
                    error={errors.businessRegion?.message?.toString()}
                    register={register("businessRegion")}
                    required
                />

                <SelectInput
                    label="District"
                    displayName="label"
                    valueName="value"
                    options={districtOptions(watch("businessRegion"))}
                    error={errors.businessDistrict?.message?.toString()}
                    register={register("businessDistrict")}
                    required
                />

                <TextInput
                    label="Street"
                    placeholder="enter your street"
                    error={errors.businessStreet?.message?.toString()}
                    register={register("businessStreet")}
                />

                <div>
                    <div className="flex justify-between items-center mb-0.5">
                        <Label text="GPS code" />

                        <IconButton
                            type="button"
                            intent="primary"
                            onClick={getLocation}
                            loading={isLocating}
                        >
                            <MapPinIcon className="size-4 text-primary" />
                        </IconButton>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <TextInput
                            type="text"
                            placeholder="lat"
                            error={errors.businessLat?.message?.toString()}
                            register={register("businessLat")}
                        />

                        <TextInput
                            type="text"
                            placeholder="lng"
                            error={errors.businessLng?.message?.toString()}
                            register={register("businessLng")}
                        />
                    </div>
                </div>

                <h4 className="text-secondary font-semibold col-span-2 pt-6">
                    Contact Information
                </h4>

                <TextInput
                    label="Manager Name"
                    placeholder="e.g. Peter Mosha"
                    className="col-span-2"
                    error={errors.businessManager?.message?.toString()}
                    register={register("businessManager")}
                    required
                />

                <PhoneNumberInput
                    name="businessPhone"
                    label="Phone Number"
                    placeholder="enter the business phone number"
                    control={control}
                    required
                />

                <TextInput
                    label="Email Address"
                    placeholder="e.g. mosha.peter@gmail.com"
                    type="email"
                    error={errors.businessEmail?.message?.toString()}
                    register={register("businessEmail")}
                    required
                />

                <div className="col-span-2 flex justify-end">
                    <Button
                        type="submit"
                        className="min-w-32"
                        intent="primary"
                        loading={isPending}
                        disabled={isLoading || !account}
                    >
                        Save Changes
                    </Button>
                </div>
            </form>
        </div>
    )
}
